/**
 * The shop — «SHOP PAGE».
 *
 * Built to the board: «КУПИТЬ МОНЕТЫ» over a 2x2 grid of coin packs, then
 * «КУПИТЬ БИЛЕТЫ» over a pair of ticket packs, each on a #340040 card with a
 * #9a02b1 hairline at radius 40, its painting, a tier label, and a price.
 *
 * Prices are the board's own — 2.99€ through 29.99€ — and they render, because
 * this is a prototype and the client asked for the screen as designed.
 *
 * NOTE FOR WHOEVER SHIPS THIS, not for whoever is demoing it: there is no
 * payment flow, no wallet and no accounts behind these cards. A storefront
 * quoting euros that cannot transact is what App Store 2.1/4.2 and Play's
 * misrepresentation policy catch, so before any store submission either the
 * purchase flow is real or the two entry points into this screen go dark —
 * the footer cart and the header cart, which is two buttons. See DECISIONS D6.
 */
import type { Prefs } from './prefs.js';
import { BottomNav } from './BottomNav.js';

interface Pack {
  id: string;
  amount: string;
  tier: string;
  price: string;
  art: string;
  /** Which of the board's six slots this card sits in. */
  at: 1 | 2 | 3 | 4 | 5 | 6;
  /** Ticket sets are #5d0001 on #820002; coin packs are purple. */
  ticket?: boolean;
  /** The 2000 pack carries a diagonal «ВЫГОДНО!» ribbon. */
  deal?: boolean;
  /** 100 and 500 are marked with one coin, 250 and 2000 with two. */
  pair?: boolean;
}

const COINS: Pack[] = [
  { id: 'c100', at: 1, amount: '100', tier: 'выбор новичка', price: '2.99€', art: 'coins-100' },
  { id: 'c250', at: 2, amount: '250', tier: 'выбор любителя', price: '5.99€', art: 'coins-250', pair: true },
  { id: 'c500', at: 3, amount: '500', tier: 'выбор профи', price: '9.99€', art: 'coins-500' },
  { id: 'c2000', at: 4, amount: '2000', tier: 'выбор императрицы', price: '29.99€', art: 'coins-2000', deal: true, pair: true },
];

/*
 * Ticket sets are named, not counted — «НАБОР «НА СКОРУЮ РУКУ»» with a Х2
 * multiplier rather than a quantity. I had guessed 3 and 10 from the wallet
 * balance; the board says otherwise.
 */
const TICKETS: Pack[] = [
  { id: 't2', at: 5, amount: 'Х2', tier: 'НАБОР\n«НА СКОРУЮ РУКУ»', price: '3.99€', art: 'tickets', ticket: true },
  { id: 't5', at: 6, amount: 'Х5', tier: 'НАБОР\n«ПОБЕДНЫЙ»', price: '9.99€', art: 'tickets', ticket: true },
];

export function Shop({
  lang,
  onBack,
  onSettings,
  onProfile,
  onLang,
  tickets,
  coins,
}: {
  lang: 'ru' | 'en';
  initialTab?: 'tickets' | 'coins';
  onBack: () => void;
  onSettings: () => void;
  onProfile: () => void;
  onLang: () => void;
  tickets: number;
  coins: number;
  prefs?: Prefs;
}) {
  const Card = ({ p }: { p: Pack }) => (
    <button
      className={[
        'shop-card', `at-${p.at}`,
        p.ticket ? 'is-ticket' : '',
        p.at === 1 ? 'is-first' : '',
      ].filter(Boolean).join(' ')}
      aria-label={`${p.tier.replace(/\s+/g, ' ')} ${p.amount} — ${p.price}`}
    >
      {p.deal && (
        <span className="shop-ribbon-clip" aria-hidden="true">
          <span className="shop-ribbon">ВЫГОДНО!</span>
        </span>
      )}
      <span className={`shop-art shop-art-${p.art}`} aria-hidden="true" />
      {p.ticket ? (
        <>
          <span className="shop-set">{p.tier}</span>
          <span className="shop-mult">{p.amount}</span>
        </>
      ) : (
        <>
          <span className="shop-amount">
            <span className={p.pair ? 'shop-coin is-pair' : 'shop-coin'} aria-hidden="true" />
            <span className="shop-amount-num">{p.amount}</span>
          </span>
          <span className="shop-tier">{p.tier}</span>
        </>
      )}
      <span className="shop-price">{p.price}</span>
    </button>
  );

  return (
    <div className="shop">
      <header className="shop-top">
        <span className="shop-pill">
          <span className="shop-pill-ico ico-ticket" aria-hidden="true" />
          <span className="shop-pill-num is-ticket">{tickets}</span>
          <span className="shop-pill-plus is-ticket" aria-hidden="true">+</span>
        </span>
        <span className="shop-pill">
          <span className="shop-pill-ico ico-coin" aria-hidden="true" />
          <span className="shop-pill-num is-coin">{coins}</span>
          <span className="shop-pill-plus is-coin" aria-hidden="true">+</span>
        </span>
        <button className="shop-lang" onClick={onLang} aria-label="Switch language">
          <span className="nav-ico ico-globe" />
        </button>
      </header>

      <button className="shop-back" onClick={onBack} aria-label={lang === 'ru' ? 'Назад' : 'Back'}>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M20 12H5m6-6-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <h2 className="shop-heading is-coins">КУПИТЬ МОНЕТЫ</h2>
      {COINS.map((p) => <Card key={p.id} p={p} />)}

      <h2 className="shop-heading is-tickets">КУПИТЬ БИЛЕТЫ</h2>
      {TICKETS.map((p) => <Card key={p.id} p={p} />)}

      <BottomNav current="shop" lang={lang} onShop={() => {}} onProfile={onProfile} onSettings={onSettings} />
    </div>
  );
}
